import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { getCall, postCall, putCall, deleteCall } from "../../utils/api";
import toast from "react-hot-toast";

const getErrorMessage = (errors) => {
  if (errors?.city_name?.[0]) return errors.city_name[0];
  if (errors?.latitude?.[0]) return errors.latitude[0];
  if (errors?.longitude?.[0]) return errors.longitude[0];
  if (errors?.region?.[0]) return errors.region[0];
  return "Failed to add/update city. Please try again.";
};

const initialState = {
  data: {
    data: [], // array of offers
    pagination: null, // optional, in case response includes pagination
  },
  offerDetail: null,
  error: null,
  status: "idle",
  isUpdateStatus: "idle",
};

export const getDubaiCity = createAsyncThunk(
  "dontMissOut/getDubaiCity",
  async ({ query = "", page = 1, per_page = 50 }) => {
    try {
      const response = await getCall(
        `/admin/v1/dubai_city?query=${query}&page=${page}&per_page=${per_page}`,
        false
      );
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  }
);

export const addCity = createAsyncThunk("city/add", async (data) => {
  try {
    return await postCall("/admin/v1/dubai_city", data, false);
  } catch (error) {
    toast.error(getErrorMessage(error?.response?.data?.errors) || "Failed to add city. Please try again.");
    return Promise?.reject(error);
  }
});

export const updateCity = createAsyncThunk(
    "city/update",
    async ({ id, formData }, { rejectWithValue }) => {
      try {
        return await putCall(`/admin/v1/dubai_city/${id}`, formData);
      } catch (error) {
        toast.error(getErrorMessage(error?.response?.data?.errors) || "Failed to add city. Please try again.");
        return rejectWithValue(error.response?.data);
      }
    }
);

export const deleteCity = createAsyncThunk("city/delete", async (id) => {
  try {
    await deleteCall(`/admin/v1/dubai_city/${id}`);
    return id;
  } catch (error) {
    return Promise?.reject(error);
  }
});

const citySlice = createSlice({
  name: "city",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Get Dubai City
      .addCase(getDubaiCity.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(getDubaiCity.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.data.data = action.payload.data.records;
        state.data.pagination = action.payload.data.pagination;
      })
      .addCase(getDubaiCity.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message || "Failed to fetch data.";
      })
      // Add City
      .addCase(addCity.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(addCity.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.data.data.push(action.payload);
      })
      .addCase(addCity.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message || "Failed to add city.";
      })

      // Update City
      .addCase(updateCity.pending, (state) => {
        state.isUpdateStatus = "loading";
        state.error = null;
      })
      .addCase(updateCity.fulfilled, (state, action) => {
        state.isUpdateStatus = "succeeded";
        const index = state.data.data.findIndex(
            (city) => city.id === action.payload.data.id
        );
        if (index >= 0) {
          state.data.data[index] = action.payload.data;
        }
      })
      .addCase(updateCity.rejected, (state, action) => {
        state.isUpdateStatus = "failed";
        state.error = action.error.message || "Failed to update city.";
      })

      // Delete City
      .addCase(deleteCity.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(deleteCity.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.data.data = state.data.data.filter(
            (city) => city.id !== action.payload
        );
      })
      .addCase(deleteCity.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message || "Failed to delete city.";
      });
  },
});

export default citySlice.reducer;
